import { TypedDocumentNode, gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { LoadMoreButton } from 'atoms/buttons/LoadMoreButton';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyM, HeadlineM } from 'atoms/typography';
import { Dialog } from 'components/dialog';
import { Avatar } from 'components/user/Avatar';
import { UserName } from 'components/user/UserName';
import { extractConnectionData } from 'gql/extractData';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { usePaginatedQuery } from 'hooks/graphql/usePaginatedQuery';

import {
  BlockedUsersQuery,
  BlockedUsersQueryVariables,
} from './__generated__/index.graphql';
import { useUnblockUser } from './useUnblockUser';

type BlockedUsersQuery_currentUser_blockedUsers_nodes = NonNullable<
  BlockedUsersQuery['currentUser']
>['blockedUsers']['nodes'][number];

const messages = defineMessages({
  title: {
    id: 'Settings.blockedUsers.dialog.title',
    defaultMessage: 'Blocked Managers',
  },
  cta: {
    id: 'Settings.blockedUsers.dialog.cta',
    defaultMessage: 'Unblock',
  },
  empty: {
    id: 'Settings.blockedUsers.dialog.empty',
    defaultMessage: 'No blocked manager.',
  },
});

const BLOCKED_USERS_QUERY = gql`
  query BlockedUsersQuery($cursor: String) {
    currentUser {
      slug
      blockedUsers(first: 10, after: $cursor) {
        nodes {
          slug
          nickname
          ...Avatar_publicUserInfoInterface
          ...UserName_publicUserInfoInterface
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Avatar.fragments.publicUserInfoInterface}
  ${UserName.fragments.user}
` as TypedDocumentNode<BlockedUsersQuery, BlockedUsersQueryVariables>;

const StyledNickname = styled(BodyM)`
  flex-grow: 1;
`;

const BlockedUser = ({
  user,
  removeBlockedUser,
}: {
  user: BlockedUsersQuery_currentUser_blockedUsers_nodes;
  removeBlockedUser: (
    user: BlockedUsersQuery_currentUser_blockedUsers_nodes
  ) => Promise<void>;
}) => {
  return (
    <Horizontal>
      <Avatar user={user} />
      <StyledNickname bold>
        <UserName user={user} />
      </StyledNickname>
      <Button
        onClick={() => {
          removeBlockedUser(user);
        }}
        size="small"
        color="quaternary"
      >
        <FormattedMessage {...messages.cta} />
      </Button>
    </Horizontal>
  );
};
const Body = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--unit) var(--triple-unit);
`;
const Footer = styled.div`
  padding: var(--triple-unit);
`;

export const BlockedUsersDialog = ({ onClose }: { onClose: () => void }) => {
  const { unblockUser } = useUnblockUser();
  const { up: isTablet } = useScreenSize('tablet');
  const { loading, data, loadMore } = usePaginatedQuery(BLOCKED_USERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    connection: 'UserConnection',
  });

  const removeBlockedUser = useCallback(
    async (user: BlockedUsersQuery_currentUser_blockedUsers_nodes) => {
      await unblockUser({
        variables: {
          input: {
            userSlug: user.slug,
          },
        },
        update: cache => {
          const dataFromCache = cache.readQuery<BlockedUsersQuery>({
            query: BLOCKED_USERS_QUERY,
          });
          const updatedData = dataFromCache?.currentUser?.blockedUsers && {
            ...dataFromCache,
            currentUser: {
              ...dataFromCache.currentUser,
              blockedUsers: {
                ...dataFromCache.currentUser.blockedUsers,
                nodes: dataFromCache.currentUser.blockedUsers.nodes.filter(
                  u => u.slug !== user.slug
                ),
              },
            },
          };
          cache.writeQuery({
            query: BLOCKED_USERS_QUERY,
            data: updatedData,
          });
          return cache;
        },
      });
    },
    [unblockUser]
  );

  const { items, cursor, hasMore } = useMemo(
    () =>
      extractConnectionData(data?.currentUser?.blockedUsers, user => (
        <BlockedUser user={user} removeBlockedUser={removeBlockedUser} />
      )),
    [data?.currentUser?.blockedUsers, removeBlockedUser]
  );

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open
      onClose={onClose}
      fullScreen={!isTablet}
      title={
        <HeadlineM as="h2" className="text-center">
          <FormattedMessage {...messages.title} />
        </HeadlineM>
      }
      footer={
        <>
          {hasMore && (
            <Footer>
              <LoadMoreButton
                color="primary"
                hasMore={hasMore}
                loading={loading}
                loadMore={() => {
                  loadMore(false, { cursor });
                }}
                fullWidth
              />
            </Footer>
          )}
        </>
      }
    >
      <Body>
        {items && items?.length > 0 && items}
        {!loading && items && items?.length === 0 && (
          <BodyM color="var(--c-nd-800)">
            <FormattedMessage {...messages.empty} />
          </BodyM>
        )}
      </Body>
    </Dialog>
  );
};
